import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, Zap } from 'lucide-react';
import { SKUS_50 as SKUS, ASSETS } from '../lib/products';
import PackImg from '../components/PackImg';

const MOODS = ['GYM', 'GAME', 'STUDY', 'WORK', 'MATCH DAY', 'ROAD TRIP'];

export default function MoodArch() {
  const [q, setQ] = useState('');
  const navigate = useNavigate();

  return (
    <section id="moods" data-testid="mood-section" className="relative bg-cream pt-6 pb-16 md:pb-24 overflow-hidden">
      <svg viewBox="0 0 1000 240" className="w-full max-w-5xl mx-auto px-2" aria-hidden="true">
        <path id="mood-arc" d="M 40 220 A 720 720 0 0 1 960 220" fill="none" />
        <text fill="#7A1E1E" fontSize="64" style={{ fontFamily: 'Bebas Neue', letterSpacing: '0.03em' }}>
          <textPath href="#mood-arc" startOffset="50%" textAnchor="middle">WHAT ARE YOU FUELLING TODAY?</textPath>
        </text>
      </svg>

      <form data-testid="mood-search-form" onSubmit={(e) => { e.preventDefault(); navigate('/products'); }}
        className="relative z-10 max-w-2xl mx-auto -mt-16 md:-mt-24 px-5">
        <div className="flex items-center gap-4 rounded-full bg-white border-2 border-cocoa px-6 py-4 shadow-[5px_5px_0_#2A1010]">
          <Search size={26} className="text-cocoa/60 shrink-0" />
          <input data-testid="mood-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="The answer is always protein..."
            className="flex-1 bg-transparent outline-none text-lg md:text-2xl text-cocoa placeholder:text-cocoa/40" />
        </div>
        <div className="flex flex-wrap justify-center gap-2 mt-5">
          {MOODS.map((m) => (
            <button key={m} type="button" data-testid={`mood-chip-${m.toLowerCase().replace(/\s/g, '-')}`} data-cursor="plus" onClick={() => setQ(`${m} MODE`)}
              className={`rounded-full border-2 border-cocoa px-4 py-1.5 font-cond font-bold text-sm tracking-wider transition-colors duration-200 flex items-center gap-1.5 ${q.startsWith(m) ? 'bg-cocoa text-kyellow' : 'bg-transparent hover:bg-butter'}`}>
              <Zap size={12} /> {m}
            </button>
          ))}
        </div>
      </form>

      <div className="relative mt-12">
        <div className="absolute inset-x-0 top-10 bottom-0 bg-[repeating-linear-gradient(135deg,transparent_0_30px,rgba(42,16,16,0.05)_30px_32px)] rounded-t-[3rem]" />
        <div className="relative z-10 flex flex-wrap justify-center gap-8 md:gap-16 px-6 md:px-14 py-6">
          {SKUS.map((sku, i) => (
            <motion.div key={sku.key} data-testid={`mood-product-${sku.key}`} className="w-52 md:w-64 text-center group"
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
              <Link to={`/product/${sku.flavor.slug}`} data-cursor="expand" data-cursor-color={sku.flavor.color}>
                <div className="relative">
                  <span className="absolute -top-1 right-0 z-10 w-14 h-14 rounded-xl bg-cocoa text-cream flex flex-col items-center justify-center leading-none rotate-6">
                    <span className="text-[7px] tracking-wider">50G</span><span className="font-display text-lg">₹50</span>
                  </span>
                  <motion.div whileHover={{ scale: 1.08, rotate: -3 }} transition={{ type: 'spring', stiffness: 260, damping: 16 }}>
                    <PackImg f={sku.flavor} className="h-48 md:h-60 mx-auto" />
                  </motion.div>
                </div>
                <p className="font-display text-2xl text-cocoa mt-3 leading-tight">{sku.flavor.name}</p>
                <p className="text-[10px] font-bold tracking-[0.2em] text-cocoa/50">{sku.flavor.tagline}</p>
                <span className="inline-block mt-2 font-cond text-xs font-bold tracking-widest underline underline-offset-4 decoration-2 group-hover:text-kred transition-colors">VIEW PRODUCT</span>
              </Link>
            </motion.div>
          ))}
          <motion.div data-testid="mood-product-flavour-3" className="w-52 md:w-64 text-center" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}>
            <Link to="/vote" data-cursor="expand" className="block">
              <img src={ASSETS.soon} alt="Flavour #3 coming soon" loading="lazy" className="h-48 md:h-60 mx-auto object-contain gpu" />
              <p className="font-display text-2xl text-cocoa mt-3 leading-tight">COMING SOON</p>
              <p className="text-[10px] font-bold tracking-[0.2em] text-cocoa/50">STAY TUNED · YOU DECIDE</p>
              <span className="inline-block mt-2 font-cond text-xs font-bold tracking-widest underline underline-offset-4 decoration-2 text-kred">VOTE NOW</span>
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
